import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Select from 'react-select'
import { add_holiday } from '../api/holidaysApi'
import { addHoliday, addHolidayInitValues } from '../../../modules/accounts/components/settings/SettingsModel'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { formatDateFlatpickr } from '../../../helper-functions/DateFunction'
import { useGeneralSettings } from '../../../contexts/GeneralSettingsContext'
import { get_all_users_filter } from '../../users/api/usersApi'

interface User {
    id: number
    name: string
    [key: string]: any
}


const holidayDetailsSchema = Yup.object().shape({
    title: Yup.string().required('Holiday Name is required'),
    from_date: Yup.date().nullable().required('From Date is required'),
    to_date: Yup.date().nullable().required('To Date is required'),
})

const AddHoliday: React.FC = () => {
  const { getSettingValue } = useGeneralSettings();
  const userDateFormat = getSettingValue('date_format') || 'dd-mm-yyyy';
  const userTimeFormat = getSettingValue('time_format') || '12hr';
  const userTimeZone = getSettingValue('time_zone') || 'UTC';
  

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const location = useLocation()
    const preSelectedUser = (location.state as { user?: User })?.user || null

    const [loading, setLoading] = useState(false)
    const [userOptions, setUserOptions] = useState<{ value: number; label: string }[]>([])


    const { data: allUsers, isLoading: isUsersLoading } = useQuery(
        ['users', '1'],
        () => get_all_users_filter('1'),
        {
            onSuccess: (res) => {
                const options = res.data.data.map((u: any) => ({
                    value: u.id,
                    label: u.fullname || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                }))
                setUserOptions(options)
            },
            onError: () => {
                toast.error('Failed to fetch users')
            },
        }
    )

    const mutation = useMutation(add_holiday, {
        onSettled: (data, error: any) => {
            setLoading(false)
            if (!error) {
                toast.success(data.message || 'Holiday added successfully!', {
                    position: 'top-right',
                    autoClose: 3000,
                })
                navigate('/holidays/list')
                queryClient.invalidateQueries('Holiday')
            } else {
                const errorMessage = error?.response?.data?.message || 'Error occurred'
                toast.error(errorMessage, {
                    position: 'top-right',
                    autoClose: 4000,
                })
            }
        },
    })

    const formik = useFormik<addHoliday>({
        initialValues: {
            ...addHolidayInitValues,
            user_id: preSelectedUser ? preSelectedUser.id : null,
        },
        validationSchema: holidayDetailsSchema,
        onSubmit: (values) => {
            setLoading(true)
            const from_date = values.from_date ? formatDateFlatpickr(values.from_date) : ''
            const to_date = values.to_date ? formatDateFlatpickr(values.to_date) : ''

            const payload = {
                title: values.title,
                from_date,
                to_date,
                description: values.description,
                ...(values.user_id ? { user_id: values.user_id } : {}),
            }

            mutation.mutate(payload)
        },
    })

    const mapDateFormat = (format: string) => {
        return format.replace(/dd/g, 'dd').replace(/mm/g, 'MM').replace(/yyyy/g, 'yyyy')
    }

    return (
        <div className='card mb-5 mb-xl-10'>
            <div className='card-header border-0'>
                <div className='card-title m-0'>
                    <h3 className='fw-bolder m-0'>Add Holiday</h3>
                </div>
            </div>

            <div id='kt_account_profile_details' className='collapse show'>
                <form onSubmit={formik.handleSubmit} noValidate className='form'>
                    <div className='card-body border-top p-9'>

                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                <span>User</span>
                            </label>
                            <div className='col-lg-8 fv-row'>
                                {preSelectedUser ? (
                                    <input
                                        type='text'
                                        className='form-control form-control-lg form-control-solid'
                                        value={preSelectedUser.fullname}
                                        disabled
                                    />
                                ) : (
                                    <Select
                                        className='react-select-styled'
                                        options={userOptions}
                                        isLoading={isUsersLoading}
                                        placeholder='For All Users'
                                        onChange={(option) => formik.setFieldValue('user_id', option ? option.value : null)}
                                        value={userOptions.find((opt) => opt.value === formik.values.user_id) || null}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Holiday Name */}
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Holiday</label>
                            <div className='col-lg-8 fv-row'>
                                <input
                                    type='text'
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder='Holiday'
                                    {...formik.getFieldProps('title')}
                                />
                                {formik.touched.title && formik.errors.title && (
                                    <div className='fv-help-block'>{formik.errors.title}</div>
                                )}
                            </div>
                        </div>

                        {/* From Date */}
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label fw-bold fs-6 required'>From Date</label>
                            <div className='col-lg-8 fv-row'>
                                <DatePicker
                                    selected={formik.values.from_date}
                                    onChange={(date: Date | null) => formik.setFieldValue('from_date', date)}
                                    className='form-control form-control-lg form-control-solid'
                                    placeholderText='From Date'
                                    dateFormat={mapDateFormat(userDateFormat)}
                                    wrapperClassName='w-100'
                                />
                                {formik.touched.from_date && formik.errors.from_date && (
                                    <div className='fv-help-block'>{formik.errors.from_date}</div>
                                )}
                            </div>
                        </div>

                        {/* To Date */}
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label fw-bold fs-6 required'>To Date</label>
                            <div className='col-lg-8 fv-row'>
                                <DatePicker
                                    selected={formik.values.to_date}
                                    onChange={(date: Date | null) => formik.setFieldValue('to_date', date)}
                                    className='form-control form-control-lg form-control-solid'
                                    placeholderText='To Date'
                                    dateFormat={mapDateFormat(userDateFormat)}
                                    minDate={formik.values.from_date || undefined}
                                    wrapperClassName='w-100'
                                />
                                {formik.touched.to_date && formik.errors.to_date && (
                                    <div className='fv-help-block'>{formik.errors.to_date}</div>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className='row mb-6'>
                            <label className='col-lg-4 col-form-label fw-bold fs-6'>Description</label>
                            <div className='col-lg-8 fv-row'>
                                <textarea
                                    className='form-control form-control-lg form-control-solid'
                                    placeholder='Description'
                                    {...formik.getFieldProps('description')}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='card-footer d-flex justify-content-end py-6 px-9'>
                        <button type='submit' className='btn btn-primary' disabled={loading || !formik.isValid}>
                            {!loading && 'Add Holiday'}
                            {loading && (
                                <span className='indicator-progress d-block'>
                                    Please wait...{' '}
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AddHoliday